import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { requestApi } from "@/api/client";

const MAX = { name: 100, company: 100, email: 254, message: 500 };

export function FinalCTA() {
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value.slice(0, MAX[k]) }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await requestApi("/api/demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al enviar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="cta" className="relative overflow-hidden py-24 sm:py-32">
      <div className="blur-blob -left-20 top-10 h-96 w-96 bg-primary/30" />
      <div className="blur-blob -right-20 bottom-0 h-96 w-96 bg-secondary/30" />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-[2.5rem] border border-border bg-card/80 p-10 shadow-elevated backdrop-blur sm:p-16">

          {done ? (
            <div className="text-center space-y-4 py-8">
              <CheckCircle2 size={48} className="mx-auto text-primary" />
              <h2 className="text-2xl font-semibold text-foreground">¡Solicitud recibida!</h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                Nos pondremos en contacto contigo pronto para coordinar la demo.
              </p>
            </div>
          ) : (
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              {/* Left — copy */}
              <div>
                <h2 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                  Cuidar a las personas no es un coste.{" "}
                  <span className="text-gradient-brand">Es una ventaja competitiva.</span>
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  Solicita una demo y te mostramos cómo Hummany puede ayudar a tu equipo.
                </p>
                <p className="mt-8 text-sm text-muted-foreground">
                  Estamos construyendo una nueva forma de cuidar a las personas en el trabajo.
                </p>
              </div>

              {/* Right — form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <p className="text-sm text-destructive bg-destructive/10 rounded-2xl px-4 py-2">{error}</p>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Nombre *</label>
                    <input
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Tu nombre"
                      className="w-full rounded-2xl border border-border bg-background px-4 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-[10px] text-muted-foreground text-right tabular-nums">{form.name.length}/{MAX.name}</p>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Empresa *</label>
                    <input
                      required
                      value={form.company}
                      onChange={set("company")}
                      placeholder="Nombre de tu empresa"
                      className="w-full rounded-2xl border border-border bg-background px-4 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-[10px] text-muted-foreground text-right tabular-nums">{form.company.length}/{MAX.company}</p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Email de trabajo *</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="tu@empresa.com"
                    className="w-full rounded-2xl border border-border bg-background px-4 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">¿Algo que quieras contarnos? <span className="text-muted-foreground/60">(opcional)</span></label>
                  <div className="relative">
                    <textarea
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tamaño del equipo, reto principal..."
                      rows={3}
                      className={cn(
                        "w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none resize-none",
                        "focus:ring-2 focus:ring-primary pb-6"
                      )}
                    />
                    <span className="absolute bottom-2 right-3 text-[10px] text-muted-foreground tabular-nums bg-gradient-to-l from-background via-background/90 to-transparent pl-4">
                      {form.message.length}/{MAX.message}
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full gradient-brand px-6 py-3 text-sm font-medium text-white shadow-glow transition hover:opacity-95 disabled:opacity-60"
                >
                  {loading ? "Enviando..." : <>Solicitar demo <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
